// Product picker — compact bar that opens an enhanced palette with
// brand chips and stock filter (top-sellers strip removed per user).
// Extracted from QuoteBuilder.jsx (Wave K refactor).
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, Search, X } from "lucide-react";
import api from "../../../lib/api";
import { GBP, exVatFromInc } from "../_quoteHelpers";
import FacetMultiSelect from "./FacetMultiSelect";

export default function ProductPicker({ onAdd }) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brandFilter, setBrandFilter] = useState([]);
  const [catFilter, setCatFilter] = useState([]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sortBy, setSortBy] = useState("BCProduct.total_sold");
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Brand + category facets, loaded once.
  useEffect(() => {
    api.get("/quotes/product-facets")
      .then(({ data }) => { setBrands(data.brands || []); setCategories(data.categories || []); })
      .catch(() => {});
  }, []);

  const runQuery = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page: 1, limit: 20, sort: sortBy, order: sortBy === "BCProduct.price" ? "asc" : "desc" });
      if (q && q.trim()) p.set("search", q.trim());
      if (brandFilter.length) p.set("brand", brandFilter.join(","));
      if (catFilter.length) p.set("category", catFilter.join(","));
      if (inStockOnly) p.set("in_stock", "true");
      const { data } = await api.get(`/lab/products?${p.toString()}`);
      setItems((data.items || []).map((it) => ({ ...it, _key: it._id })));
      setActive(0);
    } catch { setItems([]); } finally { setLoading(false); }
  };

  // Re-query on filter change while palette is open
  useEffect(() => {
    if (!paletteOpen) return;
    runQuery();
     
  }, [q, brandFilter, catFilter, inStockOnly, sortBy, paletteOpen]);

  const openPalette = () => {
    setQ(""); setBrandFilter([]); setCatFilter([]); setInStockOnly(false); setSortBy("BCProduct.total_sold");
    setPaletteOpen(true);
    setTimeout(() => inputRef.current?.focus(), 40);
  };
  const closePalette = () => setPaletteOpen(false);

  const onSelect = (p) => {
    onAdd(p);
    closePalette();
  };

  // Keyboard nav
  useEffect(() => {
    if (!paletteOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); closePalette(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, items.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
      else if (e.key === "Enter" && items[active]) { e.preventDefault(); onSelect(items[active]); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
     
  }, [paletteOpen, items, active]);

  return (
    <>
      <div style={{
        display: "flex", alignItems: "center", gap: 8, padding: "12px 14px",
        background: "var(--t2-surface)", border: "1px solid var(--t2-border)",
        borderRadius: 12, marginBottom: 12,
      }} data-testid="product-picker-bar">
        <button
          onClick={openPalette}
          className="t2-btn-ghost"
          style={{ flex: 1, justifyContent: "flex-start", background: "var(--t2-bg)", padding: "8px 12px" }}
          data-testid="product-search-btn"
        >
          <Search className="w-3.5 h-3.5" style={{ color: "var(--t2-orange-ink)" }} />
          <span style={{ color: "var(--t2-grey3)" }}>Search SKU, brand, keyword…</span>
          <span style={{ marginLeft: "auto", color: "var(--t2-grey4)", fontSize: 10 }} className="t2-mono">
            <kbd style={{ background: "var(--t2-surface3)", padding: "1px 5px", borderRadius: 3, marginRight: 4 }}>⌘</kbd>
            <kbd style={{ background: "var(--t2-surface3)", padding: "1px 5px", borderRadius: 3 }}>K</kbd>
          </span>
        </button>
        <button type="button" onClick={openPalette} data-testid="product-search-add-btn" title="Add a product"
          aria-label="Add a product" className="qb-search-plus">
          <Plus className="w-4 h-4" strokeWidth={3} />
        </button>
      </div>

      {paletteOpen && createPortal(
        <div className="t2-cmd-overlay" onClick={closePalette} data-testid="product-palette-overlay">
          <div className="t2-cmd t2-cmd--wide" onClick={(e) => e.stopPropagation()} data-testid="product-palette">
            <div className="t2-cmd-search">
              <Search className="w-4 h-4" style={{ color: "var(--t2-orange-ink)" }} />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by SKU, brand or keyword…"
                data-testid="product-palette-input"
              />
              <kbd>ESC</kbd>
              <button type="button" onClick={closePalette} aria-label="Close search" title="Close (Esc)" data-testid="product-palette-close"
                style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 8, cursor: "pointer",
                  border: "1px solid var(--t2-border)", background: "transparent", color: "var(--t2-grey2)" }}>
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Filter row — brand chips, stock, sort */}
            <div style={{
              padding: "10px 14px", borderBottom: "1px solid var(--t2-border)",
              background: "var(--t2-bg2)", display: "flex", flexDirection: "column", gap: 8,
            }} data-testid="product-palette-filters">
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <FacetMultiSelect label="Brand" options={brands} selected={brandFilter} onChange={setBrandFilter} testid="brand" />
                <FacetMultiSelect label="Category" options={categories} selected={catFilter} onChange={setCatFilter} testid="category" />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer" }} data-testid="in-stock-toggle-wrap">
                  <input
                    type="checkbox"
                    checked={inStockOnly}
                    onChange={(e) => setInStockOnly(e.target.checked)}
                    data-testid="in-stock-toggle"
                  />
                  <span className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey2)", textTransform: "uppercase", letterSpacing: ".06em" }}>In stock only</span>
                </label>
                <span className="t2-mono" style={{ fontSize: 9, color: "var(--t2-brass-ink)", letterSpacing: ".08em", textTransform: "uppercase" }}>Sort:</span>
                {[
                  { k: "BCProduct.total_sold", l: "Top sellers" },
                  { k: "BCProduct.price",      l: "Price ↑" },
                  { k: "BCProduct.date_modified", l: "Newest" },
                ].map(s => (
                  <button
                    key={s.k}
                    onClick={() => setSortBy(s.k)}
                    className={`t2-chip ${sortBy === s.k ? "orange" : ""}`}
                    style={{ cursor: "pointer", padding: "4px 9px" }}
                    data-testid={`sort-${s.k.split(".").pop()}`}
                  >{s.l}</button>
                ))}
              </div>
            </div>

            <div className="t2-cmd-row t2-cmd-row--cols t2-cmd-colhead" data-testid="product-palette-colhead">
              <span>SKU</span><span>Product</span>
              <span style={{ textAlign: "right" }}>Cost</span><span style={{ textAlign: "right" }}>Sell ex VAT</span>
              <span style={{ textAlign: "right" }}>Sell inc VAT</span><span style={{ textAlign: "right" }}>Margin</span><span style={{ textAlign: "right" }}>Sold</span>
            </div>
            <div className="t2-cmd-list t2-scroll">
              {loading && <div className="t2-cmd-empty">Searching…</div>}
              {!loading && items.length === 0 && <div className="t2-cmd-empty" data-testid="product-palette-empty">No products match — try widening the filters.</div>}
              {!loading && items.map((p, i) => <PaletteRow key={p._id || i} p={p} i={i} active={i === active} onSelect={onSelect} onHover={setActive} />)}
            </div>
            <div className="t2-cmd-foot">
              <span><kbd>↑↓</kbd> Navigate <kbd>↵</kbd> Select</span>
              <span data-testid="product-palette-count">{items.length} match{items.length === 1 ? "" : "es"}</span>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

// One product row — SKU · name/brand/category/stock · cost · sell ex/inc · margin · units sold.
function PaletteRow({ p, i, active, onSelect, onHover }) {
  const bc = p.BCProduct || {};
  const tm = p.TMProduct || {};
  const isTest = p._lab_test_data === true;
  const sellEx = exVatFromInc(bc.price);
  const cost = Number(bc.cost_price) || 0;
  const margin = sellEx && cost > 0 ? ((sellEx - cost) / sellEx) * 100 : null;
  const mTone = margin == null ? "var(--t2-grey4)" : margin < 5 ? "var(--t2-red-ink, #f87171)" : margin < 12 ? "var(--t2-brass-ink)" : "var(--t2-teal-ink)";
  const sold = bc.total_sold ?? tm.TotalSold;
  return (
    <div className={`t2-cmd-row t2-cmd-row--cols ${active ? "active" : ""}`} onClick={() => onSelect(p)} onMouseEnter={() => onHover(i)} data-testid={`product-palette-row-${i}`}>
      <div className="t2-cmd-sku" style={{ color: isTest ? "var(--t2-teal)" : "var(--t2-orange)" }}>{bc.sku}</div>
      <div style={{ minWidth: 0 }}>
        <div className="t2-cmd-name" title={bc.name}>{bc.name}</div>
        <div className="t2-cmd-meta">
          {tm.BrandName && <span>{tm.BrandName}</span>}
          {tm.ProductLabel && <span title={tm.MMCatPath || ""} data-testid={`product-palette-cat-${i}`} style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 4, background: "rgba(47,200,203,0.1)", color: "var(--t2-teal-ink)" }}>{tm.ProductLabel}</span>}
          {bc.inventory_level != null && <span> · Stock {Number(bc.inventory_level).toLocaleString("en-GB")}</span>}
          {isTest && <span style={{ color: "var(--t2-teal-ink)" }}> · TEST</span>}
        </div>
      </div>
      <div className="t2-cmd-num" data-testid={`product-palette-cost-${i}`}>{cost > 0 ? GBP(cost) : "—"}</div>
      <div className="t2-cmd-num t2-cmd-num--hero" data-testid={`product-palette-sell-${i}`}>{GBP(sellEx)}</div>
      <div className="t2-cmd-num">{GBP(bc.price)}</div>
      <div className="t2-cmd-num" style={{ color: mTone, fontWeight: 800 }}>{margin == null ? "—" : `${margin.toFixed(1)}%`}</div>
      <div className="t2-cmd-num" data-testid={`product-palette-sold-${i}`}>{sold != null ? Number(sold).toLocaleString("en-GB") : "—"}</div>
    </div>
  );
}
